import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  ColumnFiltersState,
  RowSelectionState,
  RowData,
} from "@tanstack/react-table";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";
import Input from "./Input";
import Loader from "./Loader";

// Per-column filter config, declared on a column's `meta`. When `filterKey` is
// set the table renders a "contains" filter input under that column header and
// reports changes (keyed by filterKey) through onColumnFilterChange.
export interface ColumnFilterMeta {
  filterKey?: string;
  filterType?: "text" | "select";
  filterOptions?: { label: string; value: string }[];
  filterPlaceholder?: string;
  // Server-side sort key. Only used when the table gets `onSortChange`; then
  // only columns with a sortKey are sortable.
  sortKey?: string;
}

// Active server-side sort reported through `onSortChange` (null = unsorted).
export interface ServerSort {
  key: string;
  desc: boolean;
}

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<
    TData extends RowData,
    TValue,
  > extends ColumnFilterMeta {}
}

// Fallback size of the column-filter panel, used to clamp its position on the
// very first paint (before it is in the DOM and can be measured). Keep in sync
// with the panel's `w-56` class.
const FILTER_PANEL_WIDTH = 224;
const FILTER_PANEL_HEIGHT = 48;
// Space between the funnel button and the panel, and the smallest gap the panel
// keeps from the viewport edges.
const FILTER_PANEL_GAP = 4;
const FILTER_VIEWPORT_MARGIN = 8;

export interface Action<T> {
  label: string;
  icon?: React.ReactNode;
  onClick: (row: T) => void;
  variant?: "primary" | "success" | "danger" | "warning";
  disabled?: (row: T) => boolean;
  visible?: ((row: T) => boolean) | boolean;
  tooltip?: string | ((row: T) => string);
}

export interface BulkAction {
  label: string;
  icon?: React.ReactNode;
  onClick: (selectedIds: string[]) => void;
  variant?: "primary" | "danger" | "warning";
}

interface TableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  actions?: Action<T>[];
  searchable?: boolean;
  pagination?: boolean;
  className?: string;
  containerClassName?: string;
  isLoading?: boolean;
  onSearch?: (searchTerm: string) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  selectable?: boolean;
  getRowId?: (row: T) => string;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: React.Dispatch<
    React.SetStateAction<RowSelectionState>
  >;
  bulkActions?: BulkAction[];
  infinite?: boolean;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  filterSlot?: React.ReactNode;
  maxHeight?: string;
  // Enables a per-column "contains" filter row under the header. Columns opt in
  // via their `meta.filterKey`. Active values are reported (debounced) as a
  // { [filterKey]: value } map.
  columnFiltersEnabled?: boolean;
  onColumnFilterChange?: (filters: Record<string, string>) => void;
  // Enables server-side sorting. Header clicks are reported as the column's
  // `meta.sortKey` instead of reordering `data`. Client-side sorting only sees
  // the rows already loaded, which is wrong for server-paged or infinite lists.
  onSortChange?: (sort: ServerSort | null) => void;
}

const Table = <T extends object>({
  data,
  columns,
  actions,
  searchable = true,
  pagination = true,
  className = "",
  containerClassName = "",
  isLoading,
  onSearch,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  selectable,
  getRowId,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  bulkActions,
  infinite = false,
  onLoadMore,
  hasNextPage = false,
  isFetchingMore = false,
  filterSlot,
  maxHeight,
  columnFiltersEnabled = false,
  onColumnFilterChange,
  onSortChange,
}: TableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [colFilterValues, setColFilterValues] = useState<
    Record<string, string>
  >({});
  // Per-column filter popover: which column's funnel is open and where to anchor
  // the floating panel (rendered via portal so the scroll container can't clip
  // it). Both null = closed.
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const filterPopoverRef = useRef<HTMLDivElement | null>(null);
  // Root of this table instance and the element that scrolls it horizontally.
  // The funnel button is looked up inside the root by filter key rather than
  // held as a node ref: a refetch remounts the header, which would leave a node
  // ref pointing at a detached element (whose rect is all zeros, sending the
  // panel to the top-left corner).
  const tableRootRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const closeFilterPopover = useCallback(() => {
    setOpenFilterKey(null);
    setFilterAnchor(null);
  }, []);

  // Place the panel under its funnel button, clamped to the viewport. Without
  // the clamp a right-most column (LAST ON TIME) or a header near the bottom of
  // the window renders the panel half off-screen. `keyOverride` is for the open
  // click, which runs before the state update lands.
  const positionFilterPopover = useCallback(
    (keyOverride?: string) => {
      const key = keyOverride ?? openFilterKey;
      if (!key) return;
      const trigger = tableRootRef.current?.querySelector<HTMLElement>(
        `[data-filter-trigger="${key}"]`,
      );
      // Header not in the DOM right now (a refetch swapped the table for the
      // loader). Leave the panel where it is; it re-anchors once the header is
      // back, so typing a filter value doesn't make it jump.
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();

      // The header cell scrolled out of view — horizontally past the table's
      // own overflow container, or off-screen with the page. Following it would
      // leave the panel floating over unrelated content, so close instead.
      // Skipped for zero-size rects, which mean "not laid out" rather than
      // "off-screen" (a header mid-render, or jsdom under test).
      const clipRect = scrollContainerRef.current?.getBoundingClientRect();
      const clip =
        clipRect && (clipRect.width > 0 || clipRect.height > 0)
          ? clipRect
          : null;
      if (rect.width > 0 || rect.height > 0) {
        const visible = {
          left: Math.max(0, clip ? clip.left : 0),
          right: Math.min(window.innerWidth, clip ? clip.right : Infinity),
          top: Math.max(0, clip ? clip.top : 0),
          bottom: Math.min(window.innerHeight, clip ? clip.bottom : Infinity),
        };
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        if (
          centerX < visible.left ||
          centerX > visible.right ||
          centerY < visible.top ||
          centerY > visible.bottom
        ) {
          closeFilterPopover();
          return;
        }
      }

      const panel = filterPopoverRef.current;
      const width = panel?.offsetWidth || FILTER_PANEL_WIDTH;
      const height = panel?.offsetHeight || FILTER_PANEL_HEIGHT;

      const left = Math.max(
        FILTER_VIEWPORT_MARGIN,
        Math.min(rect.left, window.innerWidth - width - FILTER_VIEWPORT_MARGIN),
      );
      // Below the button by default, flipped above it when the bottom of the
      // window is in the way.
      let top = rect.bottom + FILTER_PANEL_GAP;
      if (top + height > window.innerHeight - FILTER_VIEWPORT_MARGIN) {
        const above = rect.top - height - FILTER_PANEL_GAP;
        top =
          above >= FILTER_VIEWPORT_MARGIN
            ? above
            : window.innerHeight - height - FILTER_VIEWPORT_MARGIN;
      }
      top = Math.max(FILTER_VIEWPORT_MARGIN, top);

      setFilterAnchor((prev) =>
        prev && prev.top === top && prev.left === left ? prev : { top, left },
      );
    },
    [openFilterKey, closeFilterPopover],
  );

  // Keep the panel glued to its trigger. Capture-phase scroll so the table's
  // own overflow container counts, not just the page; layout effect so the
  // re-anchor lands before paint instead of flashing at the old spot. `data`
  // and `isLoading` are dependencies because new rows can change column widths,
  // which moves the header cell the panel is anchored to.
  useLayoutEffect(() => {
    if (!openFilterKey) return;
    const reposition = () => positionFilterPopover();
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [openFilterKey, positionFilterPopover, data, isLoading]);

  // Close the filter popover on outside click or Escape.
  useEffect(() => {
    if (!openFilterKey) return;
    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        filterPopoverRef.current &&
        !filterPopoverRef.current.contains(target) &&
        !(target instanceof Element && target.closest("[data-filter-trigger]"))
      ) {
        closeFilterPopover();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeFilterPopover();
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [openFilterKey, closeFilterPopover]);

  const onColumnFilterChangeRef = useRef(onColumnFilterChange);
  const onPageChangeRef = useRef(onPageChange);
  useEffect(() => {
    onColumnFilterChangeRef.current = onColumnFilterChange;
  }, [onColumnFilterChange]);
  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  // Debounce per-column filter changes before notifying the parent (which
  // refetches server-side). Mirrors the global search debounce behavior.
  const didMountColFilters = useRef(false);
  useEffect(() => {
    if (!columnFiltersEnabled) return;
    if (!didMountColFilters.current) {
      didMountColFilters.current = true;
      return;
    }
    const handle = setTimeout(() => {
      onColumnFilterChangeRef.current?.(colFilterValues);
      // Reset to page 1 so filtered results start from the top.
      if (onPageChangeRef.current && page !== undefined) {
        onPageChangeRef.current(1);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [colFilterValues, columnFiltersEnabled, page]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingMoreRef = useRef(isFetchingMore);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);
  useEffect(() => {
    hasNextPageRef.current = hasNextPage;
  }, [hasNextPage]);
  useEffect(() => {
    isFetchingMoreRef.current = isFetchingMore;
  }, [isFetchingMore]);

  useEffect(() => {
    if (!infinite) return;
    const node = sentinelRef.current;
    if (!node) return;

    // When maxHeight is set the table body scrolls inside its own container,
    // so the observer/scroll listeners must target that container rather than
    // the window — otherwise "load more" would never trigger.
    const scrollRoot = maxHeight ? scrollContainerRef.current : null;
    const scrollTarget: HTMLElement | Window = scrollRoot ?? window;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry?.isIntersecting &&
          hasNextPageRef.current &&
          !isFetchingMoreRef.current
        ) {
          onLoadMoreRef.current?.();
        }
      },
      { root: scrollRoot, rootMargin: "200px" },
    );

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      observer.observe(node);
    };

    const onFirstScroll = () => {
      start();
      scrollTarget.removeEventListener("scroll", onFirstScroll);
      scrollTarget.removeEventListener("wheel", onFirstScroll);
      scrollTarget.removeEventListener("touchmove", onFirstScroll);
      scrollTarget.removeEventListener("keydown", onKeyScroll as EventListener);
    };
    const onKeyScroll = (e: KeyboardEvent) => {
      if (
        e.key === "PageDown" ||
        e.key === "End" ||
        e.key === "ArrowDown" ||
        e.key === " "
      ) {
        onFirstScroll();
      }
    };

    scrollTarget.addEventListener("scroll", onFirstScroll, { passive: true });
    scrollTarget.addEventListener("wheel", onFirstScroll, { passive: true });
    scrollTarget.addEventListener("touchmove", onFirstScroll, {
      passive: true,
    });
    scrollTarget.addEventListener("keydown", onKeyScroll as EventListener);

    return () => {
      observer.disconnect();
      scrollTarget.removeEventListener("scroll", onFirstScroll);
      scrollTarget.removeEventListener("wheel", onFirstScroll);
      scrollTarget.removeEventListener("touchmove", onFirstScroll);
      scrollTarget.removeEventListener("keydown", onKeyScroll as EventListener);
    };
    // isLoading is a dependency because the scroll container and sentinel are
    // only mounted once loading finishes; without it the observer would attach
    // to null refs on first run and never re-attach.
  }, [infinite, maxHeight, isLoading]);

  const selectedCount = controlledRowSelection
    ? Object.keys(controlledRowSelection).filter(
        (k) => controlledRowSelection[k],
      ).length
    : 0;
  const selectedIds = controlledRowSelection
    ? Object.keys(controlledRowSelection).filter(
        (k) => controlledRowSelection[k],
      )
    : [];

  const selectColumn: ColumnDef<T> = {
    id: "select",
    header: ({ table }) => (
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-primary-default focus:ring-primary-default cursor-pointer"
        checked={table.getIsAllPageRowsSelected()}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-primary-default focus:ring-primary-default cursor-pointer"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    enableSorting: false,
  };

  const actionsColumn: ColumnDef<T> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) =>
      !actions ? null : (
        <div className="flex items-center space-x-2">
          {actions
            .filter((action) => {
              if (action.visible === undefined) return true;
              if (typeof action.visible === "boolean") {
                return action.visible;
              }
              return action.visible(row.original) !== false;
            })
            .map((action, index) => {
              const tooltipText = action.label ?? "";
              return (
                <div key={index} className="relative group" title={tooltipText}>
                  <button
                    onClick={() => action.onClick(row.original)}
                    disabled={action.disabled?.(row.original)}
                    className={`
                      p-2 text-lg font-medium rounded-lg
                      ${
                        action.variant === "danger"
                          ? "bg-danger-light text-danger-default hover:bg-danger-default hover:text-white"
                          : action.variant === "success"
                            ? "bg-success-light text-success-default hover:bg-success-default  hover:text-white"
                            : action.variant === "warning"
                              ? "bg-warning-light text-warning-default hover:bg-warning-default hover:text-white"
                              : "bg-info-light text-info-default hover:bg-info-default hover:text-white"
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {action.icon && <span>{action.icon}</span>}
                  </button>
                  {tooltipText && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {tooltipText}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ),
  };

  const serverSorting = typeof onSortChange === "function";

  const allColumns = [
    ...(selectable ? [selectColumn] : []),
    ...(serverSorting
      ? columns.map((column) =>
          column.meta?.sortKey ? column : { ...column, enableSorting: false },
        )
      : columns),
    ...(actions && actions.length > 0 ? [actionsColumn] : []),
  ];

  // Client-side paging only applies when the caller asked for pagination and
  // isn't driving it themselves (server paging via page/pageSize/total, or
  // infinite scroll). Without the `pagination` check the row model would still
  // be sliced to 10 rows while the pager controls stayed hidden, silently
  // truncating the table.
  const useInternalPagination =
    pagination &&
    !infinite &&
    (page === undefined || pageSize === undefined || total === undefined);
  const useBackendSearch = typeof onSearch === "function";
  const table = useReactTable({
    data,
    columns: allColumns,
    ...(getRowId ? { getRowId: (row: T) => getRowId(row) } : {}),
    enableRowSelection: selectable,
    onRowSelectionChange: selectable ? onRowSelectionChange : undefined,
    state: {
      sorting,
      columnFilters,
      ...(useBackendSearch ? {} : { globalFilter }),
      ...(selectable && controlledRowSelection
        ? { rowSelection: controlledRowSelection }
        : {}),
    },
    onSortingChange: serverSorting
      ? (updater) => {
          const next =
            typeof updater === "function" ? updater(sorting) : updater;
          setSorting(next);
          const active = next[0];
          const key = active
            ? table.getColumn(active.id)?.columnDef.meta?.sortKey
            : undefined;
          onSortChange?.(key ? { key, desc: active.desc } : null);
        }
      : setSorting,
    manualSorting: serverSorting,
    onColumnFiltersChange: setColumnFilters,
    ...(useBackendSearch
      ? {}
      : {
          onGlobalFilterChange: setGlobalFilter,
          getFilteredRowModel: getFilteredRowModel(),
        }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(useInternalPagination
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          initialState: {
            pagination: {
              pageSize: 10,
            },
          },
        }
      : {}),
  });

  return (
    <div ref={tableRootRef} className={`space-y-2 ${containerClassName}`}>
      {(searchable || filterSlot) && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {searchable && (
            <div className="relative flex-1 max-w-sm">
              <Input
                placeholder="Search..."
                value={useBackendSearch ? searchValue : globalFilter}
                onChange={(e) => {
                  if (useBackendSearch) {
                    const newSearchValue = e.target.value;
                    setSearchValue(newSearchValue);
                    onSearch && onSearch(newSearchValue);
                    // Reset to page 1 when search value changes
                    // This ensures that when user searches from any page, results start from page 1
                    if (onPageChange && page !== undefined) {
                      onPageChange(1);
                    }
                  } else {
                    setGlobalFilter(e.target.value);
                  }
                }}
                icon={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
              />
            </div>
          )}
          {filterSlot && (
            <div className="flex items-center gap-1 flex-wrap ml-auto">
              {filterSlot}
            </div>
          )}
        </div>
      )}

      {selectable && selectedCount > 0 && bulkActions && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-gray-700">
            {selectedCount} item{selectedCount > 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2">
            {bulkActions.map((action, index) => (
              <button
                key={index}
                onClick={() => action.onClick(selectedIds)}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                  ${
                    action.variant === "danger"
                      ? "bg-danger-default text-white hover:bg-red-700"
                      : action.variant === "warning"
                        ? "bg-warning-default text-white hover:bg-yellow-600"
                        : "bg-primary-default text-white hover:bg-primary-light"
                  }
                `}
              >
                {action.icon && <span>{action.icon}</span>}
                {action.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => onRowSelectionChange?.({})}
            className="ml-auto inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
            Clear
          </button>
        </div>
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <div
          ref={scrollContainerRef}
          className={`overflow-x-auto ${maxHeight ? "overflow-y-auto" : ""} rounded-lg border border-gray-200 ${className}`}
          style={maxHeight ? { maxHeight } : undefined}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-200 sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const filterKey = columnFiltersEnabled
                      ? header.column.columnDef.meta?.filterKey
                      : undefined;
                    const isFilterActive =
                      !!filterKey && !!colFilterValues[filterKey]?.trim();
                    return (
                      <th
                        key={header.id}
                        className={`
                          bg-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider
                          ${header.column.getCanSort() ? "cursor-pointer" : ""}
                        `}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center space-x-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getCanSort() && (
                            <span className="flex flex-col ml-1">
                              <ChevronUpIcon
                                className={`h-2 w-2 ${
                                  header.column.getIsSorted() === "asc"
                                    ? "text-primary-700"
                                    : "text-gray-600"
                                }`}
                              />
                              <ChevronDownIcon
                                className={`h-2 w-2 ${
                                  header.column.getIsSorted() === "desc"
                                    ? "text-primary-700"
                                    : "text-gray-600"
                                }`}
                              />
                            </span>
                          )}
                          {filterKey && (
                            <button
                              type="button"
                              data-filter-trigger={filterKey}
                              title="Filter"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openFilterKey === filterKey) {
                                  closeFilterPopover();
                                  return;
                                }
                                // First placement (estimated panel size); the
                                // layout effect refines it once it can measure.
                                positionFilterPopover(filterKey);
                                setOpenFilterKey(filterKey);
                              }}
                              className={`ml-1 rounded p-0.5 transition-colors ${
                                isFilterActive
                                  ? "text-primary-default"
                                  : "text-gray-400 hover:text-gray-600"
                              }`}
                            >
                              <Bars3Icon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={table.getVisibleLeafColumns().length || 1}
                    className="text-center py-12"
                  >
                    <div className="text-gray-500 text-lg font-medium mb-2">
                      No data found
                    </div>
                    <div className="text-gray-400 text-sm">
                      There are no items to display at the moment.
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr className="hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-4 py-2 whitespace-nowrap text-md text-gray-700"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
          {infinite && maxHeight && (
            <div ref={sentinelRef} aria-hidden="true" className="h-1" />
          )}
        </div>
      )}

      {openFilterKey &&
        filterAnchor &&
        createPortal(
          (() => {
            const openColumn = table
              .getAllLeafColumns()
              .find((c) => c.columnDef.meta?.filterKey === openFilterKey);
            const meta = openColumn?.columnDef.meta;
            const value = colFilterValues[openFilterKey] ?? "";
            return (
              <div
                ref={filterPopoverRef}
                style={{
                  position: "fixed",
                  top: filterAnchor.top,
                  left: filterAnchor.left,
                  maxWidth: `calc(100vw - ${FILTER_VIEWPORT_MARGIN * 2}px)`,
                  zIndex: 60,
                }}
                className="w-56 rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
              >
                {meta?.filterType === "select" ? (
                  <select
                    autoFocus
                    value={value}
                    onChange={(e) =>
                      setColFilterValues((prev) => ({
                        ...prev,
                        [openFilterKey]: e.target.value,
                      }))
                    }
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  >
                    <option value="">All</option>
                    {meta?.filterOptions?.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex items-center gap-1">
                    <input
                      autoFocus
                      type="text"
                      value={value}
                      onChange={(e) =>
                        setColFilterValues((prev) => ({
                          ...prev,
                          [openFilterKey]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") closeFilterPopover();
                      }}
                      placeholder={meta?.filterPlaceholder ?? "Filter…"}
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                    />
                    {value && (
                      <button
                        type="button"
                        title="Clear"
                        onClick={() =>
                          setColFilterValues((prev) => ({
                            ...prev,
                            [openFilterKey]: "",
                          }))
                        }
                        className="rounded p-1 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })(),
          document.body,
        )}

      {infinite && (
        <>
          {!maxHeight && (
            <div ref={sentinelRef} aria-hidden="true" className="h-1" />
          )}
          {/* {isFetchingMore && (
            <div className="text-center text-sm text-gray-500">
              Loading more…
            </div>
          )}
          {!hasNextPage && !isFetchingMore && data.length > 0 && (
            <div className="text-center text-xs text-gray-400">End of list</div>
          )} */}
        </>
      )}

      {pagination && !infinite && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-md text-gray-700">
              Page{" "}
              {useInternalPagination
                ? table.getState().pagination.pageIndex + 1
                : page}{" "}
              of{" "}
              {useInternalPagination
                ? table.getPageCount()
                : Math.ceil((total || 0) / (pageSize || 10))}
            </span>
            <select
              value={
                useInternalPagination
                  ? table.getState().pagination.pageSize
                  : pageSize
              }
              onChange={(e) => {
                const newSize = Number(e.target.value);
                if (useInternalPagination) {
                  table.setPageSize(newSize);
                } else if (onPageSizeChange) {
                  onPageSizeChange(newSize);
                }
              }}
              className="rounded-lg border border-gray-300 text-sm px-2 py-1"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="p-1 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {
                if (useInternalPagination) {
                  table.previousPage();
                } else if (onPageChange && page && page > 1) {
                  onPageChange(page - 1);
                }
              }}
              disabled={
                useInternalPagination
                  ? !table.getCanPreviousPage()
                  : (page || 1) <= 1
              }
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              className="p-1 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {
                if (useInternalPagination) {
                  table.nextPage();
                } else if (
                  onPageChange &&
                  page &&
                  total &&
                  page < Math.ceil(total / (pageSize || 10))
                ) {
                  onPageChange(page + 1);
                }
              }}
              disabled={
                useInternalPagination
                  ? !table.getCanNextPage()
                  : !(
                      page &&
                      total &&
                      page < Math.ceil(total / (pageSize || 10))
                    )
              }
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
